import { MapOutlined } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import isNil from "lodash/isNil";
import { useEffect, useRef, useState } from "react";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { buildStaticMapUrl } from "./GoogleMapPanel";
import { loadGoogleMaps } from "./loadGoogleMaps";

/**
 * Interactive Google Maps panel — used by PropertyMap's aerial mode.
 * Uses the Maps JavaScript API so the user can drag, pan, and zoom.
 *
 * The radius circle is drawn via `google.maps.Circle` centered on the
 * property, so the ring stays anchored to the address while the map pans
 * underneath it. Save flows use the Static API: on every center/zoom
 * change we rebuild a matching Static URL (center = current map center,
 * zoom = current zoom, radius circle still anchored to property) and
 * emit it via `onViewChange`. PropertyMap stores the latest URL so
 * "Save as image" / "Save image to loan" capture the exact current view.
 *
 * Requires REACT_APP_GOOGLE_MAPS_KEY with Maps JavaScript API enabled.
 */
const GoogleMapPanelInteractive = ({
  address,
  height = "200px",
  mapType = "hybrid",
  radiusMiles = null,
  zoom,
  onViewChange,
  // When true, clicks on the map fire onPinDrop instead of doing nothing.
  // Used by PropertyMap to let the user precisely locate raw-land /
  // new-subdivision properties whose initial coords are a wide ZIP-centroid
  // approximation. Cursor flips to crosshair to signal click-to-place.
  pinDropMode = false,
  onPinDrop,
  sx,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const latitude = address?.latitude;
  const longitude = address?.longitude;

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const circleRef = useRef(null);
  const markerRef = useRef(null);
  const onViewChangeRef = useRef(onViewChange);
  const onPinDropRef = useRef(onPinDrop);
  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);
  useEffect(() => {
    onPinDropRef.current = onPinDrop;
  }, [onPinDrop]);

  const [status, setStatus] = useState("loading");

  // Init map once per address change. Zoom, mapType, and radiusMiles
  // are applied via separate effects below so that updates don't
  // destroy/recreate the map instance (and lose the user's pan).
  useEffect(() => {
    let cancelled = false;

    if (!apiKey) {
      setStatus("error");
      return;
    }
    if (isNil(latitude) || isNil(longitude)) {
      setStatus("error");
      return;
    }

    setStatus("loading");

    loadGoogleMaps()
      .then((google) => {
        if (cancelled) return;
        if (!containerRef.current) {
          console.error(
            "[GoogleMapPanelInteractive] Container ref was unmounted before init",
          );
          setStatus("error");
          return;
        }

        const center = { lat: latitude, lng: longitude };
        let map;
        try {
          map = new google.maps.Map(containerRef.current, {
            center,
            zoom: !isNil(zoom) ? zoom : 15,
            mapTypeId: mapType,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy", // scroll-zoom without Ctrl
            clickableIcons: false,
          });
        } catch (err) {
          console.error(
            "[GoogleMapPanelInteractive] Map constructor threw:",
            err,
          );
          setStatus("error");
          return;
        }
        mapRef.current = map;

        const marker = new google.maps.Marker({ position: center, map });
        markerRef.current = marker;

        const emit = () => {
          if (!onViewChangeRef.current) return;
          const mapCenter = map.getCenter();
          const mapZoom = map.getZoom();
          if (!mapCenter) return;
          const url = buildStaticMapUrl({
            apiKey,
            latitude: mapCenter.lat(),
            longitude: mapCenter.lng(),
            mapType,
            zoom: mapZoom,
            radiusMiles,
          });
          onViewChangeRef.current(url);
        };
        emit();
        map.addListener("center_changed", emit);
        map.addListener("zoom_changed", emit);

        setStatus("ready");
      })
      .catch((err) => {
        console.error(
          "[GoogleMapPanelInteractive] Failed to load Maps JS API:",
          err,
        );
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (mapRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
      if (circleRef.current) circleRef.current.setMap(null);
      if (markerRef.current) markerRef.current.setMap(null);
      mapRef.current = null;
      circleRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, latitude, longitude]);

  // Sync controlled zoom prop (the +/- buttons in PropertyMap).
  useEffect(() => {
    if (!mapRef.current || isNil(zoom)) return;
    if (mapRef.current.getZoom() !== zoom) mapRef.current.setZoom(zoom);
  }, [zoom]);

  // Sync mapType prop.
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapType);
  }, [mapType]);

  // Sync radius circle: remove old one, draw new (or none).
  // `status` is in the dep list so this re-runs when the map finishes
  // initializing — on first mount the map instance isn't ready yet when
  // this effect first fires, so without the status dep the initial circle
  // would never be drawn (only changing the radius later would trigger
  // the redraw).
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (radiusMiles && radiusMiles > 0 && !isNil(latitude) && !isNil(longitude)) {
      circleRef.current = new window.google.maps.Circle({
        map: mapRef.current,
        center: { lat: latitude, lng: longitude },
        radius: radiusMiles * 1609.34,
        fillColor: "#2962ff",
        fillOpacity: 0.15,
        strokeColor: "#2962ff",
        strokeOpacity: 0.85,
        strokeWeight: 2,
        clickable: false,
      });
    }
  }, [radiusMiles, latitude, longitude, status]);

  // When radius changes, recenter on the property so the new scope is
  // anchored around the subject even if the user had panned away.
  useEffect(() => {
    if (!mapRef.current || isNil(latitude) || isNil(longitude)) return;
    mapRef.current.panTo({ lat: latitude, lng: longitude });
  }, [radiusMiles, latitude, longitude]);

  // Pin-drop mode: register a click listener that converts the click
  // event's latLng into the new pin position, and switch the cursor to
  // crosshair so the user sees the map is now click-to-place.
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    map.setOptions({
      draggableCursor: pinDropMode ? "crosshair" : null,
      draggingCursor: pinDropMode ? "crosshair" : null,
    });
    if (!pinDropMode) return undefined;
    const listener = map.addListener("click", (e) => {
      const lat = e?.latLng?.lat?.();
      const lng = e?.latLng?.lng?.();
      if (isNil(lat) || isNil(lng)) return;
      onPinDropRef.current?.({ latitude: lat, longitude: lng });
    });
    return () => {
      if (window.google?.maps?.event && listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [pinDropMode, status]);

  if (status === "error") {
    return (
      <Flx
        center
        column
        sx={{
          height,
          background: "#f5f5f5",
          gap: 1,
          ...sx,
        }}
      >
        <MapOutlined sx={{ color: "#666", fontSize: "2rem" }} />
        <Txt sx={{ color: "#666" }}>
          {!apiKey ? "Map key not configured" : "Failed to load map"}
        </Txt>
      </Flx>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height,
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {status === "loading" && (
        <Flx
          center
          sx={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            pointerEvents: "none",
          }}
        >
          <CircularProgress size={24} />
        </Flx>
      )}
    </Box>
  );
};

export default GoogleMapPanelInteractive;
